import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getDailyAffirmation } from "@/lib/affirmations";
import { Waves, CloudRain, Trees, Play, Pause, Volume2, VolumeX, X, Heart } from "lucide-react";
import { toast } from "sonner";

const SCENES = [
  {
    id: "ocean",
    name: "Ocean Waves",
    icon: Waves,
    color: "#4A90A4",
    image: "https://images.unsplash.com/photo-1507166763745-bfe008fbb831?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxjYWxtJTIwb2NlYW4lMjB3YXZlc3xlbnwwfHx8fDE3ODcxODQ1MjJ8MA&ixlib=rb-4.1.0&q=85",
    audio: "https://cdn.jsdelivr.net/npm/sounds-for-focus@0.1.0/audio/nature/waves.mp3",
    tint: "from-[#1a3a52]/60 via-[#2c5f7a]/40 to-transparent",
  },
  {
    id: "rain",
    name: "Gentle Rain",
    icon: CloudRain,
    color: "#7B8FA1",
    image: "https://images.unsplash.com/photo-1621112816926-db4caf0b37fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxnZW50bGUlMjByYWluJTIwZm9yZXN0fGVufDB8fHx8MTc4NzE4NDUyMnww&ixlib=rb-4.1.0&q=85",
    audio: "https://cdn.jsdelivr.net/npm/sounds-for-focus@0.1.0/audio/rain/light-rain.mp3",
    tint: "from-[#2a3a3a]/60 via-[#3d5555]/40 to-transparent",
  },
  {
    id: "forest",
    name: "Forest Ambience",
    icon: Trees,
    color: "#5C8A5C",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    audio: "https://cdn.jsdelivr.net/npm/sounds-for-focus@0.1.0/audio/nature/jungle.mp3",
    tint: "from-[#1e3822]/60 via-[#2f5432]/40 to-transparent",
  },
];

const DURATIONS = [5, 10, 15];

// Simple chime (Web Audio)
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99];
    notes.forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = freq;
      o.type = "sine";
      g.gain.value = 0;
      o.connect(g); g.connect(ctx.destination);
      const start = ctx.currentTime + i * 0.25;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.25, start + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 1.6);
      o.start(start);
      o.stop(start + 1.7);
    });
    setTimeout(() => ctx.close(), 3000);
  } catch {}
}

export default function Relaxation() {
  const { refresh } = useAuth();
  const [scene, setScene] = useState(SCENES[0]);
  const [duration, setDuration] = useState(5);
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(duration * 60);
  const [muted, setMuted] = useState(false);
  const [gratitude, setGratitude] = useState("");
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState("Breathe in...");
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const visitLogged = useRef(false);
  const affirmation = getDailyAffirmation();

  useEffect(() => {
    setRemaining(duration * 60);
  }, [duration]);

  // Breathing phase text
  useEffect(() => {
    if (!running) return;
    const phases = ["Breathe in...", "Hold...", "Breathe out...", "Hold..."];
    let i = 0;
    setPhase(phases[0]);
    const t = setInterval(() => {
      i = (i + 1) % phases.length;
      setPhase(phases[i]);
    }, 4000);
    return () => clearInterval(t);
  }, [running]);

  // Countdown
  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          setRunning(false);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          playChime();
          toast.success("Session complete. Well done. 🌿");
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  // Cleanup: stop audio on unmount (user leaves Relaxation Room)
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    };
  }, []);

  const start = useCallback(async () => {
    setRemaining(duration * 60);
    setRunning(true);
    const a = audioRef.current;
    if (a) {
      a.loop = true;
      a.volume = muted ? 0 : 0.55;
      // Ensure current scene src is loaded
      if (!a.src || !a.src.includes(scene.audio.split("/").pop())) {
        a.src = scene.audio;
        a.load();
      }
      try {
        await a.play();
      } catch (err) {
        toast.error("Tap again to enable audio");
      }
    }
    if (!visitLogged.current) {
      try {
        await api.post("/relaxation/visit");
        visitLogged.current = true;
        refresh();
      } catch {}
    }
  }, [duration, muted, refresh, scene]);

  const pause = () => {
    setRunning(false);
    if (audioRef.current) audioRef.current.pause();
  };

  const stop = () => {
    setRunning(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setRemaining(duration * 60);
  };

  const toggleMute = () => {
    setMuted((m) => {
      const newMuted = !m;
      if (audioRef.current) audioRef.current.volume = newMuted ? 0 : 0.55;
      return newMuted;
    });
  };

  // When scene changes: swap src, keep playing if currently running
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const wasRunning = running;
    a.pause();
    a.src = scene.audio;
    a.loop = true;
    a.volume = muted ? 0 : 0.55;
    a.load();
    if (wasRunning) {
      a.play().catch(() => {});
    }
  }, [scene]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveGratitude = async () => {
    if (!gratitude.trim()) return;
    setSaving(true);
    try {
      await api.post("/relaxation/gratitude", { text: gratitude });
      toast.success("Saved. 🌸");
      setGratitude("");
    } catch {
      toast.error("Could not save");
    }
    setSaving(false);
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="relative min-h-[calc(100vh-4rem)] lg:min-h-screen">
      {/* Background scene */}
      <div className="absolute inset-0 z-0 overflow-hidden rounded-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={scene.id}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0"
          >
            <img src={scene.image} alt="" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 bg-gradient-to-b ${scene.tint}`} />
            <div className="absolute inset-0 bg-black/30" />
          </motion.div>
        </AnimatePresence>
      </div>

      <audio ref={audioRef} loop preload="auto" data-testid="relax-audio" />

      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 py-8 lg:py-14 space-y-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-white/70">Toolbox · Take a moment</p>
            <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-bold">Relaxation Room</h1>
          </div>
          {running && (
            <Button
              onClick={stop}
              data-testid="relax-stop"
              variant="ghost"
              className="rounded-full text-white hover:bg-white/15"
            >
              <X className="w-4 h-4 mr-1" /> End
            </Button>
          )}
        </div>

        {/* Affirmation */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-3xl backdrop-blur-xl bg-white/12 border border-white/25 p-5 sm:p-6"
          data-testid="daily-affirmation"
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-white/70">Today's affirmation</p>
          <p className="mt-2 font-heading text-xl sm:text-2xl font-semibold leading-snug">"{affirmation}"</p>
        </motion.div>

        {/* Scene picker */}
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-white/70 mb-3">Pick a soundscape</p>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {SCENES.map((s) => {
              const Icon = s.icon;
              const active = scene.id === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setScene(s)}
                  data-testid={`scene-${s.id}`}
                  className={`flex flex-col items-center gap-2 rounded-2xl backdrop-blur-xl border p-3 sm:p-4 transition-colors duration-200 ${
                    active ? "bg-white/25 border-white/50" : "bg-white/10 border-white/20 hover:bg-white/18"
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs font-semibold">{s.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Duration */}
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-white/70 mb-3">Duration</p>
          <div className="flex gap-2">
            {DURATIONS.map((d) => (
              <button
                key={d}
                onClick={() => !running && setDuration(d)}
                data-testid={`duration-${d}`}
                disabled={running}
                className={`flex-1 rounded-full backdrop-blur-xl border py-3 text-sm font-semibold transition-colors duration-200 ${
                  duration === d ? "bg-white text-[#1B2A4A] border-white" : "bg-white/10 text-white border-white/20 hover:bg-white/18"
                } ${running ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {d} min
              </button>
            ))}
          </div>
        </div>

        {/* Breathing circle */}
        <div className="flex flex-col items-center justify-center py-6 sm:py-10">
          <motion.div
            animate={running ? { scale: [0.85, 1.15, 1.15, 0.85], opacity: [0.75, 1, 1, 0.75] } : { scale: 1, opacity: 0.9 }}
            transition={running ? { duration: 16, ease: "easeInOut", times: [0, 0.25, 0.75, 1], repeat: Infinity } : { duration: 0.5 }}
            className="relative w-56 h-56 sm:w-64 sm:h-64 rounded-full flex items-center justify-center"
            data-testid="breathing-circle"
            style={{ background: `radial-gradient(circle, ${scene.color}80, ${scene.color}20)` }}
          >
            <div className="absolute inset-4 rounded-full border-2 border-white/40" />
            <div className="text-center">
              <p className="font-heading text-5xl sm:text-6xl font-bold tabular-nums" data-testid="timer-display">
                {mm}:{ss}
              </p>
              <AnimatePresence mode="wait">
                <motion.p
                  key={phase}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="mt-2 text-sm font-medium text-white/85"
                >
                  {running ? phase : "Ready when you are"}
                </motion.p>
              </AnimatePresence>
            </div>
          </motion.div>

          <div className="mt-6 flex gap-2">
            {!running ? (
              <Button
                onClick={start}
                data-testid="relax-start"
                className="h-12 px-6 rounded-full bg-white text-[#1B2A4A] hover:bg-white/90 font-semibold transition-colors duration-200"
              >
                <Play className="w-4 h-4 mr-2" /> Begin
              </Button>
            ) : (
              <Button
                onClick={pause}
                data-testid="relax-pause"
                className="h-12 px-6 rounded-full bg-white text-[#1B2A4A] hover:bg-white/90 font-semibold transition-colors duration-200"
              >
                <Pause className="w-4 h-4 mr-2" /> Pause
              </Button>
            )}
            <Button
              onClick={toggleMute}
              variant="ghost"
              data-testid="relax-mute"
              className="h-12 w-12 rounded-full text-white hover:bg-white/15"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Gratitude */}
        <div className="rounded-3xl backdrop-blur-xl bg-white/12 border border-white/25 p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4 text-white/85" />
            <p className="font-heading font-semibold">What is one thing you're grateful for today?</p>
          </div>
          <Textarea
            value={gratitude}
            onChange={(e) => setGratitude(e.target.value)}
            data-testid="gratitude-input"
            rows={3}
            placeholder="A small moment, a kindness, a warm cup of coffee..."
            className="rounded-2xl bg-white/10 border-white/25 text-white placeholder:text-white/50 focus-visible:ring-2 focus-visible:ring-white/40 resize-none"
          />
          <div className="mt-3 flex justify-end">
            <Button
              onClick={saveGratitude}
              disabled={saving || !gratitude.trim()}
              data-testid="gratitude-save"
              className="rounded-full bg-[#E8621A] hover:bg-[#D05615]"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
